/**
 * AuthRecoveryBanner — global session-expiry recovery surface.
 *
 * Before this, an expired Lexiun JWT was a dead-end: every
 * `/api/platform/*` call 401'd with a raw "Invalid or expired token"
 * banner and there was no in-app way to recover (the only sign-out is
 * Clerk's gated `<UserButton>` menu). PlatformApi now calls
 * `invalidateAuth()` on a 401 — which drops the in-memory cache AND the
 * stale localStorage seed so the next call self-heals — and notifies
 * here so the operator gets an actionable recovery action instead of a
 * dead-end. Auto-clears when a fresh token is obtained.
 *
 * Mounted once in AppLayout (App.tsx) → no per-page churn.
 */

import { useEffect, useState } from "react";
import { RefreshCw, LogIn } from "lucide-react";
import { subscribeAuthState } from "@/lib/lexiun-jwt";
import { resolveAppUrl } from "@/lib/url";

const clerkPk = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY as string | undefined;

export function AuthRecoveryBanner() {
  const [expired, setExpired] = useState(false);

  useEffect(() => subscribeAuthState((s) => setExpired(s === "expired")), []);

  if (!expired) return null;

  return (
    <div
      className="mb-4 flex items-center justify-between gap-4 rounded-lg border border-warning/40 bg-warning/10 px-4 py-3"
      data-testid="session-expired-banner"
      role="alert"
    >
      <div className="flex items-center gap-2 text-sm">
        <RefreshCw className="h-4 w-4 shrink-0 text-warning" />
        <span className="text-foreground">
          <strong>Session expired.</strong>{" "}
          <span className="text-muted">
            Your sign-in is no longer valid — reconnect to continue.
          </span>
        </span>
      </div>
      {clerkPk ? (
        <a
          href={resolveAppUrl("/sign-in")}
          className="inline-flex shrink-0 items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground hover:bg-primary/90"
          data-testid="session-expired-signin"
        >
          <LogIn className="h-4 w-4" /> Sign in again
        </a>
      ) : (
        <button
          type="button"
          onClick={() => window.location.reload()}
          className="inline-flex shrink-0 items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground hover:bg-primary/90"
          data-testid="session-expired-reload"
          title="The stale token was cleared; reloading re-issues a fresh dev session"
        >
          <RefreshCw className="h-4 w-4" /> Reload to reconnect
        </button>
      )}
    </div>
  );
}
